import { Flex, Text } from "@mantine/core";
import { useMemo } from "react";

import {
  getDefaultClosingStatementWarnings,
  getDefaultOpeningStatementWarnings,
} from "./company-default-statement-warnings";
import {
  EDIT_COMPANY_MODAL_TEST_IDS,
  FIELD_IDS,
  WARNING_TEST_ID_BY_TYPE,
} from "./edit-company-modal.constants";
import { StatementWarning } from "./statement-warning";
import { TemplateTextarea } from "./template-textarea";

interface DefaultStatementsSectionProps {
  defaultClosingStatement: string;
  defaultOpeningStatement: string;
  disabled: boolean;
  setDefaultClosingStatement: (value: string) => void;
  setDefaultOpeningStatement: (value: string) => void;
}

export const DefaultStatementsSection = ({
  defaultClosingStatement,
  defaultOpeningStatement,
  disabled,
  setDefaultClosingStatement,
  setDefaultOpeningStatement,
}: DefaultStatementsSectionProps) => {
  const defaultOpeningStatementWarnings = useMemo(
    () => getDefaultOpeningStatementWarnings(defaultOpeningStatement),
    [defaultOpeningStatement],
  );
  const defaultClosingStatementWarnings = useMemo(
    () => getDefaultClosingStatementWarnings(defaultClosingStatement),
    [defaultClosingStatement],
  );

  return (
    <>
      <div>
        <Flex align="center" gap={12}>
          <Text className="shrink-0 text-xs font-bold uppercase tracking-wide text-uw-gray-6">
            Opening &amp; closing messages
          </Text>
          <div className="h-px flex-1 bg-uw-gray-1" />
        </Flex>
      </div>

      <div>
        <Flex align="center" justify="space-between" mb={8}>
          <Text
            className="text-sm font-bold text-uw-gray-9"
            component="label"
            htmlFor={FIELD_IDS.defaultOpeningStatement}
          >
            Default Intro
          </Text>
          <Text c="gray.6" className="text-xs font-medium">
            Opening statement &middot; Optional
          </Text>
        </Flex>
        <TemplateTextarea
          ariaLabel="Default Intro"
          dataTestId={EDIT_COMPANY_MODAL_TEST_IDS.introInput}
          disabled={disabled}
          id={FIELD_IDS.defaultOpeningStatement}
          minRows={3}
          onChange={setDefaultOpeningStatement}
          value={defaultOpeningStatement}
        />
        {defaultOpeningStatementWarnings.map((warning) => (
          <StatementWarning
            key={warning.type}
            testId={WARNING_TEST_ID_BY_TYPE[warning.type]}
            warning={warning}
          />
        ))}
      </div>

      <div>
        <Flex align="center" justify="space-between" mb={8}>
          <Text
            className="text-sm font-bold text-uw-gray-9"
            component="label"
            htmlFor={FIELD_IDS.defaultClosingStatement}
          >
            Default Conclusion
          </Text>
          <Text c="gray.6" className="text-xs font-medium">
            Closing statement &middot; Optional
          </Text>
        </Flex>
        <TemplateTextarea
          ariaLabel="Default Conclusion"
          dataTestId={EDIT_COMPANY_MODAL_TEST_IDS.conclusionInput}
          disabled={disabled}
          highlightVariables={false}
          id={FIELD_IDS.defaultClosingStatement}
          minRows={3}
          onChange={setDefaultClosingStatement}
          value={defaultClosingStatement}
        />
        {defaultClosingStatementWarnings.map((warning) => (
          <StatementWarning
            key={warning.type}
            testId={WARNING_TEST_ID_BY_TYPE[warning.type]}
            warning={warning}
          />
        ))}
      </div>
    </>
  );
};
